import axios from "axios";
import { getToken } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const d = (p) => p.then((r) => r.data);

export const adminApi = {
  overview: () => d(client.get("/admin/overview")),

  // --- Shared file field ---
  uploadFileImage: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/file/image", fd, { timeout: 60000 })); },
  uploadFileDocument: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/file/document", fd, { timeout: 120000 })); },
  importFileUrl: (url, kind) => d(client.post("/admin/file/import-url", { url, kind })),

  // --- Committees ---
  listCommittees: () => d(client.get("/admin/committees")),
  createCommittee: (data) => d(client.post("/admin/committees", data)),
  updateCommittee: (id, data) => d(client.put(`/admin/committees/${id}`, data)),
  duplicateCommittee: (id) => d(client.post(`/admin/committees/${id}/duplicate`)),
  committeePdf: (id) => client.get(`/admin/committees/${id}/pdf`, { responseType: "blob" }).then((r) => r.data),
  archiveCommittee: (id) => d(client.post(`/admin/committees/${id}/archive`)),
  deleteCommittee: (id) => d(client.delete(`/admin/committees/${id}`)),

  // --- GR extras ---
  adminGrs: () => d(client.get("/admin/grs")),
  grAnalytics: (id) => d(client.get(`/admin/grs/${id}/analytics`)),
  grRemind: (id) => d(client.post(`/admin/grs/${id}/remind`)),

  // --- CMS: pages ---
  listPages: () => d(client.get("/admin/pages")),
  createPage: (data) => d(client.post("/admin/pages", data)),
  updatePage: (slug, data) => d(client.put(`/admin/pages/${slug}`, data)),
  deletePage: (slug) => d(client.delete(`/admin/pages/${slug}`)),
  pageVersions: (slug) => d(client.get(`/admin/pages/${slug}/versions`)),
  restorePage: (slug, vid) => d(client.post(`/admin/pages/${slug}/restore/${vid}`)),

  // --- CMS: labels ---
  getLabels: () => d(client.get("/admin/labels")),
  putLabels: (data) => d(client.put("/admin/labels", data)),

  // --- CMS: media ---
  listMedia: () => d(client.get("/admin/media")),
  uploadMedia: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/media/upload", fd, { timeout: 60000 })); },
  deleteMedia: (id) => d(client.delete(`/admin/media/${id}`)),

  // --- CMS: settings (super admin) ---
  getSiteInfo: () => d(client.get("/admin/site-info")),
  putSiteInfo: (data) => d(client.put("/admin/site-info", data)),
  getNav: () => d(client.get("/admin/nav")),
  putNav: (data) => d(client.put("/admin/nav", data)),
  getHomeConfig: () => d(client.get("/admin/home-config")),
  putHomeConfig: (data) => d(client.put("/admin/home-config", data)),
  getFeatures: () => d(client.get("/admin/features")),
  putFeatures: (data) => d(client.put("/admin/features", data)),
  getTemplates: () => d(client.get("/admin/templates")),
  putTemplates: (data) => d(client.put("/admin/templates", data)),
  previewTemplate: (data) => d(client.post("/admin/templates/preview", data)),
  templateVersions: () => d(client.get("/admin/template-versions")),
  restoreTemplates: (vid) => d(client.post(`/admin/templates/restore/${vid}`)),
  listAdminUsers: () => d(client.get("/admin/admin-users")),
  loginProblems: () => d(client.get("/admin/login-problems")),
  loginProblemsCsvUrl: () => `${client.defaults.baseURL}/admin/login-problems.csv`,
  loginAttempts: () => d(client.get("/admin/login-attempts")),
  googleLinked: () => d(client.get("/admin/google-linked")),
  unlinkGoogle: (mid) => d(client.post(`/admin/members/${mid}/unlink-google`)),
  createAdminUser: (data) => d(client.post("/admin/admin-users", data)),
  resetAdminPassword: (uid, password) => d(client.put(`/admin/admin-users/${uid}/password`, { password })),
  changeAdminRole: (uid, role) => d(client.put(`/admin/admin-users/${uid}/role`, { role })),
  deleteAdminUser: (uid) => d(client.delete(`/admin/admin-users/${uid}`)),

  createBearer: (data) => d(client.post("/admin/office-bearers", data)),
  updateBearer: (id, data) => d(client.put(`/admin/office-bearers/${id}`, data)),
  deleteBearer: (id) => d(client.delete(`/admin/office-bearers/${id}`)),

  updateDistrict: (slug, data) => d(client.put(`/admin/districts/${slug}`, data)),

  createGr: (data) => d(client.post("/admin/grs", data)),
  updateGr: (id, data) => d(client.put(`/admin/grs/${id}`, data)),
  deleteGr: (id) => d(client.delete(`/admin/grs/${id}`)),

  createTeam: (data) => d(client.post("/admin/gapl-teams", data)),
  updateTeam: (id, data) => d(client.put(`/admin/gapl-teams/${id}`, data)),
  deleteTeam: (id) => d(client.delete(`/admin/gapl-teams/${id}`)),

  updateAd: (id, data) => d(client.put(`/admin/ads/${id}`, data)),

  createSponsor: (data) => d(client.post("/admin/sponsors", data)),
  deleteSponsor: (id) => d(client.delete(`/admin/sponsors/${id}`)),

  gaplSponsors: () => d(client.get("/gapl-sponsors")),
  createGaplSponsor: (data) => d(client.post("/admin/gapl-sponsors", data)),
  updateGaplSponsor: (id, data) => d(client.put(`/admin/gapl-sponsors/${id}`, data)),
  deleteGaplSponsor: (id) => d(client.delete(`/admin/gapl-sponsors/${id}`)),
  uploadSponsorLogo: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/sponsor-logo", fd)); },
  adRateCard: () => d(client.get("/ad-rate-card")),
  updateAdRateCard: (data) => d(client.put("/admin/ad-rate-card", data)),
  serviceFilterOptions: () => d(client.get("/admin/service/filter-options")),
  socialSettings: () => d(client.get("/admin/social-settings")),
  updateSocialSettings: (data) => d(client.put("/admin/social-settings", data)),

  receiptSignature: () => d(client.get("/admin/receipt-signature")),
  updateReceiptSignature: (data) => d(client.put("/admin/receipt-signature", data)),
  uploadReceiptSignature: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/receipt-signature/image", fd)); },
  deleteReceiptSignature: () => d(client.delete("/admin/receipt-signature/image")),
  adminReceipts: () => d(client.get("/admin/receipts")),
  regenerateReceiptPdf: (rid) => client.get(`/admin/receipts/${rid}/pdf`, { responseType: "blob" }).then((r) => r.data),

  slideshow: () => d(client.get("/admin/slideshow")),
  uploadSlide: (file, onProgress) => {
    const fd = new FormData(); fd.append("file", file);
    return d(client.post("/admin/slideshow/upload", fd, { timeout: 120000, onUploadProgress: onProgress }));
  },
  replaceSlide: (id, file, onProgress) => {
    const fd = new FormData(); fd.append("file", file);
    return d(client.post(`/admin/slideshow/${id}/replace`, fd, { timeout: 120000, onUploadProgress: onProgress }));
  },
  updateSlide: (id, data) => d(client.put(`/admin/slideshow/${id}`, data)),
  reorderSlides: (order) => d(client.put("/admin/slideshow/reorder", { order })),
  deleteSlide: (id) => d(client.delete(`/admin/slideshow/${id}`)),
  slideSettings: (data) => d(client.put("/admin/slideshow/settings", data)),

  createUpcoming: (data) => d(client.post("/admin/upcoming", data)),
  deleteUpcoming: (id) => d(client.delete(`/admin/upcoming/${id}`)),

  updateNotice: (data) => d(client.put("/admin/important-notice", data)),
  updateFooterSettings: (data) => d(client.put("/admin/footer-settings", data)),

  issues: () => d(client.get("/admin/issues")),
  updateIssue: (id, data) => d(client.put(`/admin/issues/${id}`, data)),
  deleteIssue: (id) => d(client.delete(`/admin/issues/${id}`)),

  removeSampleData: () => d(client.post("/admin/remove-sample-data")),

  members: (params) => d(client.get("/admin/members", { params })),
  createMember: (data) => d(client.post("/admin/members", data)),
  updateMember: (id, data) => d(client.put(`/admin/members/${id}`, data)),
  removeMember: (id, reason) => d(client.post(`/admin/members/${id}/remove`, { reason })),
  importMembers: (payload) => d(client.post("/admin/members/import", payload)),
  setLeaders: (slug, data) => d(client.put(`/admin/districts/${slug}/leaders`, data)),
  accessRequests: () => d(client.get("/admin/access-requests")),
  approveAccess: (id) => d(client.post(`/admin/access-requests/${id}/approve`)),
  audit: () => d(client.get("/admin/audit")),

  collections: () => d(client.get("/admin/collections")),
  createCollection: (data) => d(client.post("/admin/collections", data)),
  activateCollection: (cid) => d(client.post(`/admin/collections/${cid}/activate`)),
  pauseCollection: (cid) => d(client.post(`/admin/collections/${cid}/pause`)),
  closeCollection: (cid) => d(client.post(`/admin/collections/${cid}/close`)),
  deleteCollection: (cid) => d(client.delete(`/admin/collections/${cid}`)),
  remindUnpaid: (cid) => d(client.post(`/admin/collections/${cid}/remind`)),
  arrears: () => d(client.get("/admin/arrears")),
  renewals: () => d(client.get("/admin/renewals")),
  sendRenewals: () => d(client.post("/admin/renewals/send")),
  collectionRequests: (cid) => d(client.get(`/admin/collections/${cid}/requests`)),
  recordPayment: (rid, data) => d(client.post(`/admin/payment-requests/${rid}/record`, data)),
  cancelReceipt: (rid) => d(client.post(`/admin/receipts/${rid}/cancel`)),
  districts: () => d(client.get("/districts")),
  uploadPhoto: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/profile/photo", fd)); },
  guideUrl: () => `${API}/guides/admin`,
  getMemberService: (id) => d(client.get(`/admin/members/${id}/service`)),
  putMemberService: (id, data) => d(client.put(`/admin/members/${id}/service`, data)),
  verifyService: (id, fields, doc_url) => d(client.post(`/admin/members/${id}/service/verify`, { fields, doc_url })),
  rejectService: (id, fields, reason) => d(client.post(`/admin/members/${id}/service/reject`, { fields, reason })),
  retirementForecast: (district = "all") => d(client.get(`/admin/service/retirement-forecast?district=${district}`)),
  seniority: (district = "all") => d(client.get(`/admin/service/seniority?district=${district}`)),
  postingDuration: (district = "all") => d(client.get(`/admin/service/posting-duration?district=${district}`)),
  completeness: () => d(client.get("/admin/service/completeness")),
  getServiceSettings: () => d(client.get("/admin/service/settings")),
  putServiceSettings: (data) => d(client.put("/admin/service/settings", data)),
  exportSeniority: (fmt, district = "all") => client.get(`/admin/service/seniority.${fmt}?district=${district}`, { responseType: "blob" }).then((r) => r.data),
  exportRetirement: (fmt, district = "all") => client.get(`/admin/service/retirement.${fmt}?district=${district}`, { responseType: "blob" }).then((r) => r.data),

  // --- Letters module ---
  letterSettings: () => d(client.get("/admin/letter-settings")),
  saveLetterSettings: (data) => d(client.put("/admin/letter-settings", data)),
  letterpads: () => d(client.get("/admin/letterpads")),
  createLetterpad: (file, name) => { const fd = new FormData(); fd.append("file", file); fd.append("name", name); return d(client.post("/admin/letterpads", fd, { timeout: 120000 })); },
  updateLetterpad: (id, data) => d(client.put(`/admin/letterpads/${id}`, data)),
  activateLetterpad: (id) => d(client.post(`/admin/letterpads/${id}/activate`)),
  deleteLetterpad: (id) => d(client.delete(`/admin/letterpads/${id}`)),
  letters: (params) => d(client.get("/admin/letters", { params })),
  letter: (id) => d(client.get(`/admin/letters/${id}`)),
  nextRef: () => d(client.get("/admin/letters/next-ref")),
  createLetter: (data) => d(client.post("/admin/letters", data)),
  updateLetter: (id, data) => d(client.put(`/admin/letters/${id}`, data)),
  deleteLetter: (id) => d(client.delete(`/admin/letters/${id}`)),
  duplicateLetter: (id) => d(client.post(`/admin/letters/${id}/duplicate`)),
  issueLetter: (id) => d(client.post(`/admin/letters/${id}/issue`)),
  reopenLetter: (id) => d(client.post(`/admin/letters/${id}/reopen`)),
  importDocx: (file) => { const fd = new FormData(); fd.append("file", file); return d(client.post("/admin/letters/import-docx", fd, { timeout: 60000 })); },
  letterPdfUrl: (id, letterpadId) => `${API}/admin/letters/${id}/pdf${letterpadId ? `?letterpad_id=${letterpadId}` : ""}`,
  letterPdfBlob: (id, letterpadId) => client.get(`/admin/letters/${id}/pdf`, { params: letterpadId ? { letterpad_id: letterpadId } : {}, responseType: "blob" }).then((r) => r.data),
  sendPreview: (id, body) => d(client.post(`/admin/letters/${id}/send-preview`, body)),
  sendLetter: (id, body) => d(client.post(`/admin/letters/${id}/send`, body)),
  deliveries: (id, params) => d(client.get(`/admin/letters/${id}/deliveries`, { params })),
  resendFailed: (id) => d(client.post(`/admin/letters/${id}/resend-failed`)),
  deliveriesCsv: (id) => client.get(`/admin/letters/${id}/deliveries.csv`, { responseType: "blob" }).then((r) => r.data),
};
